import { useState, useEffect } from 'react';
import PetDisplay from './components/PetDisplay';
import StatBars from './components/StatBars';
import NetworkMetrics from './components/NetworkMetrics';
import HistoryChart from './components/HistoryChart';
import LiveStream from './components/LiveStream';
import LogWindow from './components/LogWindow';
import { usePetState } from './hooks/usePetState';

function App() {
  const { petState, metrics, loading, error, status } = usePetState();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-dark-500">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500 mx-auto"></div>
          <p className="mt-4 text-lg font-pixel text-primary-400">Loading Nostrie...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-dark-500">
        <div className="text-center text-danger-500">
          <h2 className="text-2xl font-pixel mb-2">Error</h2>
          <p className="font-mono">{error}</p>
          <p className="mt-4 font-mono">Unable to connect to Nostrie. Please try again later.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-dark-500 py-6 px-4 max-w-lg mx-auto">
      <header className="text-center mb-6">
        <div className="relative inline-block">
          <h1 className="text-3xl md:text-4xl font-pixel text-primary-400 mb-2 pulse">NOSTRIE</h1>
          <div className="absolute -inset-1 bg-primary-500 rounded-lg opacity-20 blur-lg -z-10"></div>
        </div>
        <p className="text-sm text-accent-400 font-mono">The Global Nostr Network Pet</p>
      </header>

      {status && !status.connected && (
        <div className="bg-dark-600 border-l-4 border-warning-500 p-4 mb-6 rounded">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-warning-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-warning-500 font-mono">
                {status.message || "Not connected to any Nostr relays. Pet state may not update correctly."}
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="arcade-container mb-6 scanlines screen-glow">
        <PetDisplay state={petState.currentState} />
        <StatBars stats={petState} />
        <p className="text-center mt-4 text-sm font-pixel text-accent-400">{getPetStatusMessage(petState)}</p>
      </div>

      <div className="arcade-container mb-6">
        <h2 className="text-xl font-pixel mb-4 text-primary-400">Network Health</h2>
        <div className="h-64 scanlines">
          <HistoryChart />
        </div>
        <p className="text-center mt-2 text-xs font-mono text-gray-400">
          <a href="/how-it-works" className="text-primary-400 hover:text-primary-300 underline">How It Works</a>
        </p>
      </div>

      <div className="arcade-container mb-6">
        <h2 className="text-xl font-pixel mb-4 text-primary-400">Network Stats</h2>
        <NetworkMetrics metrics={metrics} />
      </div>

      <div className="arcade-container mb-6">
        <h2 className="text-xl font-pixel mb-4 text-primary-400">Live Activity</h2>
        <LiveStream />
      </div>

      <div className="mb-6">
        <LogWindow />
      </div>

      <footer className="text-center text-gray-500 text-xs font-mono mt-8 mb-4">
        <p>Nostrie monitors the health of the Nostr network in real-time.</p>
        <p className="mt-2">
          © {new Date().getFullYear()} Nostrie Project
          <span className="mx-2">|</span>
          <a href="/raw-data-log" className="text-primary-400 hover:text-primary-300">Log</a>
        </p>
      </footer>
    </div>
  );
}

function getPetStatusMessage(stats) {
  const { currentState } = stats;
  
  const messages = {
    'starving': "Nostrie is starving! The network needs more notes.",
    'exhausted': "Nostrie is exhausted! Zap activity is very low.",
    'depressed': "Nostrie is depressed. Moscow Time (sats/USD) is falling.",
    'hungry': "Nostrie is getting hungry. Network activity is below normal.",
    'tired': "Nostrie is tired. Zap energy is lower than usual.",
    'sad': "Nostrie is feeling sad. Moscow Time is declining.",
    'excellent': "Nostrie is thriving! The Nostr network is very healthy.",
    'good': "Nostrie is feeling good. The network is in good shape.",
    'fair': "Nostrie is doing okay. Network activity is moderate.",
    'poor': "Nostrie isn't feeling great. The network could use more activity.",
    'disconnected': "Unable to connect to the Nostr network. Please check your connection."
  };
  
  return messages[currentState] || "Nostrie is monitoring the network.";
}

export default App; 